import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { BiRightArrowAlt } from "react-icons/bi";
import { Box, Flex, Text } from "rebass/styled-components";

import {
  LeagueMatchChampionImage,
  MatchRowImageContainer,
} from "../../styledComponents";
import { formatDate } from "../../utils";
import { URLS } from "../../../../Utilities/Maps";
import { TMatch, TMoment } from "MVVM/Views/MatchHistory/@types";
import { MomentsList } from "../../Components/Shared/Match/MomentsList";
import { GameSpecificColumns } from "../GameSpecificColumns";
import { useRecoilValue } from "recoil";
import {
  MatchHistoryGame,
  MatchHistoryWidgetIndentifierState,
  MatchHistoryWidgetSourceState,
} from "MVVM/State/matchHistory";
import { useTranslation } from "react-i18next";
import { PartnerViewSession } from "MVVM/State";
import { analytics } from "MVVM/Analytics";

const getTopMomentsByScore = (moments: TMoment[]) => {
  // Add index to each moment and sort by score
  const indexedMoments = moments.map((moment, index) => ({ moment, index }));
  const topThree = indexedMoments
    .sort((a, b) => b.moment.score - a.moment.score)
    .slice(0, 3)
    // Sort back by original index to maintain order
    .sort((a, b) => a.index - b.index)
    .map(({ moment }) => moment);
  return topThree;
};

export const Matches = ({
  header,
  subheader,
  matches,
}: {
  matches: TMatch[];
  header?: string;
  subheader?: string;
}) => {
  const history = useHistory();
  const matchHistoryGame = useRecoilValue(MatchHistoryGame);
  const widgetPlayerIdentifier = useRecoilValue(
    MatchHistoryWidgetIndentifierState,
  );
  const widgetSource = useRecoilValue(MatchHistoryWidgetSourceState);
  const partnerViewSession = useRecoilValue(PartnerViewSession);
  const { t } = useTranslation();
  return (
    <>
      <WidgetContainer>
        {header && (
          <Box mt={4} ml={4} variant={"text.headingMedium"}>
            {header}
          </Box>
        )}
        {subheader && (
          <Box mt={3} ml={4} variant={"text.paragraphMedium"} color="chalk">
            {subheader}
          </Box>
        )}

        {matches.map((match) => {
          const key = `id_${match.id}`;
          return (
            <>
              <MatchRow key={key}>
                <MapRow>
                  <MatchRowImageContainer style={{ width: "unset" }} mr={3}>
                    <LeagueMatchChampionImage
                      src={match.thumbnail}
                      style={{
                        maxHeight: "48px",
                      }}
                    />
                  </MatchRowImageContainer>
                  <HideColumnOnSmall>
                    <Text variant={"text.labelMedium"}>{match.title}</Text>
                    <Text color="chalk" variant={"text.labelSmall"}>
                      {formatDate(match.matchPlayedAt)}
                    </Text>
                  </HideColumnOnSmall>
                </MapRow>
                <GameSpecificColumns match={match} />
                <Column>
                  <GoToMatchContainer
                    textAlign={"center"}
                    justifyContent={"center"}
                    alignContent={"center"}
                  >
                    <Text
                      variant={"text.labelMedium"}
                      color={"envy"}
                      textAlign={"center"}
                      justifyContent={"center"}
                      alignContent={"center"}
                      onClick={() => {
                        analytics.track("Match History - Go to Match", {
                          isWidget: true,
                          widgetSource: widgetSource,
                        });
                        const matchId = match.id;
                        const game = matchHistoryGame;
                        let url = `${URLS.MATCH_HISTORY}?matchId=${matchId}&game=${game}`;

                        if (widgetPlayerIdentifier) {
                          url = `${url}&playerSearchIdentifier=${widgetPlayerIdentifier}&partner=${partnerViewSession?.clip?.partner?.partnerId?.partnerId}`;
                        }
                        if (widgetSource === "iframe") {
                          url = `${url}&ajs_aid=${window.rudderanalytics.getAnonymousId()}`;
                          window.open(url, "_blank");
                          return;
                        }

                        history.push(url);
                      }}
                    >
                      {t("mhWidget.goToMatch")}
                    </Text>
                    <RightArrow />
                  </GoToMatchContainer>
                </Column>
              </MatchRow>
              <Flex flexDirection="column" flexGrow="1" mt={"24px"}>
                <MomentsList
                  matchId={match.id}
                  isWidget={true}
                  outdated={match.outdated}
                />
              </Flex>
            </>
          );
        })}
      </WidgetContainer>
    </>
  );
};

const GoToMatchContainer = styled(Flex)`
  &:hover {
    cursor: pointer;
  }
`;
const RightArrow = styled(BiRightArrowAlt)`
  color: ${({ theme }) => theme.colors.envy};
  height: 20px;
  width: 20px;
`;
const MatchRow = styled(Flex)`
  border-top: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  justify-content: space-between;
  padding-top: 16px;
`;
const Column = styled(Flex)`
  align-items: start;
  flex-direction: column;
  gap: 6px;
  justify-content: center;
  min-width: 50px;
`;

const HideColumnOnSmall = styled(Column)`
  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    display: none;
  }
`;

const MapRow = styled(Flex)`
  gap: 5px;
  min-width: 125px;
  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    min-width: unset;
  }
`;
const WidgetContainer = styled(Flex)`
  flex-direction: column;
  width: 100%;
`;
